(function () {
  'use strict';

  const WishlistManager = {
    STORAGE_KEY: 'diamension_wishlist',
    MAX_ITEMS: 100,
    TOAST_DURATION: 4000,

    wishlist: [],
    listeners: new Set(),
    toastElement: null,
    toastTimeout: null,

    init() {
      this.load();
      this.initializeButtons();
      this.setupToast();
      this.publish('loaded', { count: this.wishlist.length });
    },

    get() {
      return [...this.wishlist];
    },

    add(productId, productData = {}) {
      if (this.has(productId)) {
        return false;
      }

      if (this.wishlist.length >= this.MAX_ITEMS) {
        console.warn('[Wishlist] Maximum items reached:', this.MAX_ITEMS);
        return false;
      }

      this.wishlist.push({
        id: String(productId),
        addedAt: Date.now(),
        ...productData,
      });

      this.save();
      this.publish('added', { productId, productData });
      this.showToast('added', productData);
      this.updateAllButtons(productId, true);
      this.updateBadge();

      return true;
    },

    remove(productId) {
      const index = this.wishlist.findIndex((item) => item.id === String(productId));
      if (index === -1) {
        return false;
      }

      const removed = this.wishlist.splice(index, 1)[0];
      this.save();
      this.publish('removed', { productId, productData: removed });
      this.showToast('removed', removed);
      this.updateAllButtons(productId, false);
      this.updateBadge();

      return true;
    },

    has(productId) {
      return this.wishlist.some((item) => item.id === String(productId));
    },

    toggle(productId, productData = {}) {
      if (this.has(productId)) {
        this.remove(productId);
        return false;
      } else {
        this.add(productId, productData);
        return true;
      }
    },

    clear() {
      this.wishlist = [];
      this.save();
      this.publish('cleared');
      this.updateBadge();
    },

    getCount() {
      return this.wishlist.length;
    },

    save() {
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.wishlist));
      } catch (error) {
        console.error('[Wishlist] Failed to save to localStorage:', error);
        try {
          sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.wishlist));
          console.warn('[Wishlist] Using sessionStorage as fallback');
        } catch (e) {
          console.error('[Wishlist] SessionStorage also failed:', e);
        }
      }
    },

    load() {
      try {
        const data = localStorage.getItem(this.STORAGE_KEY);
        this.wishlist = data ? JSON.parse(data) : [];
      } catch (error) {
        console.error('[Wishlist] Failed to load from localStorage:', error);
        this.wishlist = [];
      }
    },

    subscribe(callback) {
      this.listeners.add(callback);
      return () => this.listeners.delete(callback);
    },

    publish(event, data = {}) {
      this.listeners.forEach((callback) => {
        try {
          callback({ event, data, timestamp: Date.now() });
        } catch (error) {
          console.error('[Wishlist] Listener error:', error);
        }
      });
    },

    initializeButtons() {
      const buttons = document.querySelectorAll('[data-wishlist-toggle]');

      buttons.forEach((button) => {
        if (button.dataset.wishlistInit) return;
        button.dataset.wishlistInit = 'true';

        const productId = button.dataset.productId;

        if (this.has(productId)) {
          button.classList.add('is-active');
        }

        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleButtonClick(button);
        });
      });
    },

    handleButtonClick(button) {
      const productId = button.dataset.productId;
      const productData = {
        title: button.dataset.productTitle,
        handle: button.dataset.productHandle,
        image: button.dataset.productImage,
        price: button.dataset.productPrice,
      };

      const isNowInWishlist = this.toggle(productId, productData);

      this.animateButton(button);

      button.classList.toggle('is-active', isNowInWishlist);
      button.setAttribute(
        'aria-label',
        isNowInWishlist
          ? `Remove ${productData.title} from wishlist`
          : `Add ${productData.title} to wishlist`
      );
    },

    updateAllButtons(productId, isInWishlist) {
      const buttons = document.querySelectorAll(
        `[data-wishlist-toggle][data-product-id="${productId}"]`
      );
      buttons.forEach((button) => {
        button.classList.toggle('is-active', isInWishlist);
      });
    },

    animateButton(button) {
      if (window.gsap) {
        gsap
          .timeline()
          .to(button, {
            scale: 0.85,
            duration: 0.1,
            ease: 'power2.in',
          })
          .to(button, {
            scale: 1,
            duration: 0.15,
            ease: 'cubic-bezier(0.68, -0.55, 0.27, 1.55)',
          });
      } else {
        button.style.transform = 'scale(0.85)';
        button.style.transition = 'transform 0.1s';
        setTimeout(() => {
          button.style.transform = 'scale(1)';
          button.style.transition = 'transform 0.15s cubic-bezier(0.68, -0.55, 0.27, 1.55)';
          setTimeout(() => {
            button.style.transform = '';
            button.style.transition = '';
          }, 150);
        }, 100);
      }
    },

    setupToast() {
      this.toastElement = document.getElementById('wishlist-toast');
      if (!this.toastElement) {
        console.warn('[Wishlist] Toast element not found');
        return;
      }

      const closeBtn = this.toastElement.querySelector('.wishlist-toast__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.hideToast());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.toastElement.classList.contains('active')) {
          this.hideToast();
        }
      });

      this.toastElement.addEventListener('mouseenter', () => {
        clearTimeout(this.toastTimeout);
      });

      this.toastElement.addEventListener('mouseleave', () => {
        if (this.toastElement.classList.contains('active')) {
          this.toastTimeout = setTimeout(() => this.hideToast(), this.TOAST_DURATION);
        }
      });
    },

    showToast(action, productData) {
      if (!this.toastElement) return;

      const message = this.toastElement.querySelector('#wishlist-toast-message');
      if (message) {
        const productTitle = productData.title || 'Item';
        message.textContent =
          action === 'added'
            ? `${productTitle} added to wishlist`
            : `${productTitle} removed from wishlist`;
      }

      this.toastElement.hidden = false;
      this.toastElement.offsetHeight;
      this.toastElement.classList.add('active');

      clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => this.hideToast(), this.TOAST_DURATION);
    },

    hideToast() {
      if (!this.toastElement) return;

      this.toastElement.classList.remove('active');
      setTimeout(() => {
        this.toastElement.hidden = true;
      }, 300);

      clearTimeout(this.toastTimeout);
    },

    updateBadge() {
      const badge = document.querySelector('[data-wishlist-badge]');
      const count = this.getCount();

      if (badge) {
        badge.textContent = count;
        badge.hidden = count === 0;
      }
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => WishlistManager.init());
  } else {
    WishlistManager.init();
  }

  window.WishlistManager = WishlistManager;
})();
